import { supabase } from "@/integrations/supabase/client";

export type StorefrontTheme =
  | "modern" | "professional" | "minimal" | "moroccan" | "dark" | "atlas"
  // Bright, high-energy light themes — deliberately louder than the four
  // soft light themes above, which all sit in the same quiet register.
  | "mint" | "signal" | "lagoon";

export const SECTION_KEYS = [
  "announcement",
  "trust",
  "departures",
  "popular_routes",
  "reviews",
  "gallery",
  "faq",
  "about",
  "content",
  "vehicle",
  "coverage",
  "social",
  "book",
  "contact",
] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

export const SECTION_LABELS: Record<SectionKey, string> = {
  announcement: "Announcement",
  trust: "Trust & stats",
  departures: "Upcoming departures",
  popular_routes: "Popular routes",
  reviews: "Reviews",
  gallery: "Gallery",
  faq: "FAQ",
  about: "About",
  content: "Your own sections",
  vehicle: "Vehicle info",
  coverage: "Coverage",
  social: "Social & WhatsApp",
  book: "Book now widget",
  contact: "Contact",
};

/** i18n keys — the SAME wording the public page's nav uses, so the builder
 *  never shows an English label next to a translated live page. */
const SECTION_LABEL_KEYS: Record<SectionKey, string> = {
  announcement: "storefront.page.nav.announcement",
  trust: "storefront.page.nav.overview",
  departures: "storefront.page.nav.trips",
  popular_routes: "storefront.page.nav.pricing",
  reviews: "storefront.common.reviews",
  gallery: "storefront.page.nav.gallery",
  faq: "storefront.page.nav.faq",
  about: "storefront.page.nav.about",
  content: "storefront.page.nav.content",
  vehicle: "storefront.page.nav.services",
  coverage: "storefront.page.nav.coverage",
  social: "storefront.page.nav.social",
  book: "storefront.common.book",
  contact: "storefront.common.contact",
};

/** Localized section label for builder chrome and preview navigation. */
export function sectionLabel(key: SectionKey, t: (k: string, o?: any) => string): string {
  return t(SECTION_LABEL_KEYS[key], { defaultValue: SECTION_LABELS[key] });
}


/** Courier-authored heading overrides, per section. An absent/empty field
 *  falls back to the template's wording — so a courier only ever overrides
 *  what they actually want to rename. */
export type SectionCopyOverride = { eyebrow?: string | null; title?: string | null; subtitle?: string | null };
export type SectionCopyOverrides = Partial<Record<SectionKey, SectionCopyOverride>>;

/** Strips blanks so an emptied input reverts to the template default. */
export function normalizeSectionCopy(raw: unknown): SectionCopyOverrides {
  const out: SectionCopyOverrides = {};
  if (!raw || typeof raw !== "object") return out;
  for (const key of SECTION_KEYS) {
    const v = (raw as any)[key];
    if (!v || typeof v !== "object") continue;
    const entry: SectionCopyOverride = {};
    (["eyebrow", "title", "subtitle"] as const).forEach((f) => {
      const s = typeof v[f] === "string" ? v[f].trim() : "";
      if (s) entry[f] = s;
    });
    if (Object.keys(entry).length) out[key] = entry;
  }
  return out;
}

/** A courier-authored free-form band: a heading plus their own text.
 *  This is the escape hatch that lets a storefront say things the platform
 *  sections don't cover, without opening a second page/route surface. */
export interface ContentBlock {
  id: string;
  title: string | null;
  body: string;
}

/** Drops empty blocks and clamps lengths so a paste can't break the page. */
export function normalizeContentBlocks(raw: unknown): ContentBlock[] {
  if (!Array.isArray(raw)) return [];
  const out: ContentBlock[] = [];
  for (const item of raw.slice(0, 8)) {
    if (!item || typeof item !== "object") continue;
    const body = typeof (item as any).body === "string" ? (item as any).body.trim().slice(0, 4000) : "";
    const title = typeof (item as any).title === "string" ? (item as any).title.trim().slice(0, 80) : "";
    if (!body && !title) continue;
    out.push({
      id: typeof (item as any).id === "string" && (item as any).id ? (item as any).id : crypto.randomUUID(),
      title: title || null,
      body,
    });
  }
  return out;
}

export type SectionOrderEntry = { key: SectionKey; enabled: boolean };

// ── Social & WhatsApp links ─────────────────────────────────────────
// The courier's own public profiles, shown by the "social" section. Values
// are stored as typed handles/URLs and rendered as outbound links only —
// the platform never posts to them.
export const SOCIAL_LINK_KINDS = ["whatsapp", "instagram", "facebook", "tiktok", "linkedin", "website"] as const;
export type SocialLinkKind = (typeof SOCIAL_LINK_KINDS)[number];
export interface SocialLink { kind: SocialLinkKind; value: string }

/** Normalizes the stored jsonb into a clean, renderable list. */
export function normalizeSocialLinks(raw: unknown): SocialLink[] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  const out: SocialLink[] = [];
  for (const kind of SOCIAL_LINK_KINDS) {
    const v = (raw as Record<string, unknown>)[kind];
    if (typeof v !== "string") continue;
    const value = v.trim().slice(0, 200);
    if (value) out.push({ kind, value });
  }
  return out;
}

/** Turns a stored handle/number/URL into a safe https href. */
export function socialLinkHref(link: SocialLink): string {
  const v = link.value.trim();
  switch (link.kind) {
    case "whatsapp": {
      const digits = v.replace(/[^\d]/g, "");
      return `https://wa.me/${digits}`;
    }
    case "instagram":
      return v.startsWith("http") ? v : `https://instagram.com/${v.replace(/^@/, "")}`;
    case "facebook":
      return v.startsWith("http") ? v : `https://facebook.com/${v.replace(/^@/, "")}`;
    case "tiktok":
      return v.startsWith("http") ? v : `https://tiktok.com/@${v.replace(/^@/, "")}`;
    case "linkedin":
      return v.startsWith("http") ? v : `https://linkedin.com/in/${v.replace(/^@/, "")}`;
    case "website":
    default:
      return v.startsWith("http") ? v : `https://${v}`;
  }
}

/** Storefronts saved before a section key shipped have no entry for it, so
 *  the builder canvas would never render it and the courier could never
 *  reach its editor. Appending missing keys (hidden, so nothing appears on
 *  the live page uninvited) makes new sections discoverable in the hidden-
 *  sections tray without touching the courier's existing order/toggles. */
export function normalizeSectionOrder(raw: unknown): SectionOrderEntry[] {
  const out: SectionOrderEntry[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const key = (item as any).key;
      if (!SECTION_KEYS.includes(key)) continue;
      if (out.some((e) => e.key === key)) continue;
      out.push({ key, enabled: (item as any).enabled !== false });
    }
  }
  for (const key of SECTION_KEYS) {
    if (!out.some((e) => e.key === key)) {
      // Announcement and social are content-gated by both renderers, so they
      // are safe to enable when upgrading an older saved layout: an empty
      // section still renders nothing publicly, while populated content can
      // no longer be silently hidden just because its key shipped later.
      out.push({ key, enabled: key === "announcement" || key === "social" });
    }
  }
  return out;
}

/** Cached machine translations of a single authored string: `{ fr: "…" }`. */
export type LocaleMap = Record<string, string> | null;

export interface StorefrontRow {
  id: string;
  courier_id: string;
  /** Structure preset (see `storefrontLayout.ts`) — independent of `theme`. */
  template: string;
  /** Per-section layout picks when template = "custom" (see storefrontLayout.ts). */
  custom_layout?: Record<string, unknown> | null;
  theme: StorefrontTheme;
  accent_color: string | null;
  tagline: string | null;
  hero_headline: string | null;
  bio_richtext: string | null;
  about_richtext: string | null;
  cover_image_url: string | null;
  /** Storage path of the courier's own business logo (courier-storefront-media). */
  logo_url: string | null;
  /** How the identity renders: logo only, name only, or logo + name. */
  logo_display: "logo" | "wordmark" | "both";
  /** Personal profile photo on the storefront — couriers running a company
   *  brand can switch it off so the site reads as a business, not a profile. */
  show_avatar: boolean;
  /** Tier A hosting: when true, `alias.eurocollis.com` is the canonical host
   *  and the path URL 301s to it at the edge (see `middleware.ts`). */
  subdomain_enabled?: boolean;
  languages: string[];
  service_tags: string[];
  section_order: { key: SectionKey; enabled: boolean }[];
  /** Per-section heading overrides authored in the builder. */
  section_copy: SectionCopyOverrides;
  /** Courier-authored free-form bands, rendered in the "content" section. */
  content_blocks: ContentBlock[];
  /** Optional pinned announcement rendered above the fold of the sections. */
  announcement_text?: string | null;
  /** Courier's own public profiles (see normalizeSocialLinks). */
  social_links?: Record<string, string> | null;
  published: boolean;
  /** Unpublished builder edits. Copied into the live columns on publish. */
  draft: StorefrontDraft | null;
  /** Auto-detected authoring language of the courier's copy (en/se/fr/es/it/ar).
   *  The public page shows the original wording when the visitor's language
   *  matches this value, and reads from the *_i18n maps for all others. */
  source_language?: string | null;
  /** Cached translations of the courier's OWN copy (written by the
   *  `translate-storefront` edge route, never edited by hand here). */
  tagline_i18n?: LocaleMap;
  hero_headline_i18n?: LocaleMap;
  bio_i18n?: LocaleMap;
  about_i18n?: LocaleMap;
  section_copy_i18n?: Partial<Record<SectionKey, Record<string, Record<string, string>>>> | null;
  /** Hash of the last translated source copy — lets a republish skip the model. */
  content_hash?: string | null;
  translated_at?: string | null;
  created_at: string;
  updated_at: string;
}


/** The editable fields that make up a draft snapshot. */
export interface StorefrontDraft {
  template: string;
  /** Per-section layout picks when template = "custom" (see storefrontLayout.ts). */
  custom_layout?: Record<string, unknown> | null;
  theme: StorefrontTheme;
  accent_color: string | null;
  tagline: string | null;
  hero_headline: string | null;
  bio_richtext: string | null;
  about_richtext: string | null;
  cover_image_url: string | null;
  logo_url: string | null;
  logo_display: "logo" | "wordmark" | "both";
  show_avatar: boolean;
  languages: string[];
  service_tags: string[];
  section_order: { key: SectionKey; enabled: boolean }[];
  section_copy: SectionCopyOverrides;
  content_blocks: ContentBlock[];
  announcement_text: string | null;
  social_links: Record<string, string>;
}

/** Extract the draft-shaped payload from any storefront-ish object. */
export function draftPayloadOf(s: Partial<StorefrontRow>): StorefrontDraft {
  return {
    template: s.template || "corridor",
    custom_layout: s.template === "custom" ? ((s.custom_layout as Record<string, unknown> | null) ?? null) : null,
    theme: (s.theme || "modern") as StorefrontTheme,
    accent_color: s.accent_color || null,
    tagline: s.tagline?.trim() || null,
    hero_headline: s.hero_headline?.trim() || null,
    bio_richtext: s.bio_richtext?.trim() || null,
    about_richtext: s.about_richtext?.trim() || null,
    cover_image_url: s.cover_image_url || null,
    logo_url: s.logo_url || null,
    logo_display: (s.logo_display || "both") as "logo" | "wordmark" | "both",
    show_avatar: s.show_avatar !== false,
    languages: s.languages || [],
    service_tags: s.service_tags || [],
    // Persist a complete order. Older storefront rows predate newer section
    // keys; saving their raw array would keep dropping those keys forever.
    section_order: normalizeSectionOrder(s.section_order),
    section_copy: normalizeSectionCopy(s.section_copy),
    content_blocks: normalizeContentBlocks(s.content_blocks),
    announcement_text: s.announcement_text?.trim().slice(0, 500) || null,
    social_links: socialLinksPayloadOf(s.social_links),
  };
}

/** Draft payload for social links: only known kinds, trimmed, capped. */
export function socialLinksPayloadOf(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const l of normalizeSocialLinks(raw)) out[l.kind] = l.value;
  return out;
}


// ── Theme system ──────────────────────────────────────────────────────
// Each theme is a genuinely distinct visual world (background, not just an
// accent swap) plus a hero treatment. `resolveStorefrontTheme` is the ONLY
// place that turns a stored (theme, accent_color) pair into render tokens —
// the builder's swatches and the public page must both read through here so
// they can never drift apart.
export interface StorefrontThemeSpec {
  label: string;
  vars: {
    bg: string; surface: string; surface2: string; ink: string; muted: string;
    accent: string; border: string;
    /** Readable text/icon colour when sitting ON the accent (buttons, pills). */
    accentInk: string;
    /** Secondary hue — used for supporting stats, chart bars, alternating rails. */
    accent2: string;
    /** Success / live tone tuned to the theme instead of a generic emerald. */
    positive: string;
    /** Rating stars & highlight strokes. */
    highlight: string;
  };
  /** Hero band gradient stops — authored per theme, not derived from one accent. */
  hero_stops: [string, string, string];
  /** Typography pairing — the single biggest signal that a storefront is its
   *  own website rather than a themed panel inside EuroCollis. */
  fonts: { display: string; body: string; google: string[] };
  /** Light or dark world — drives grouping in the theme pickers. */
  tone: "light" | "dark";
  /** Hero band treatment: full corridor gradient, a flat brand panel, or a bare minimal strip. */
  hero: "gradient" | "flat" | "minimal";
  /** Card headers/eyebrows: wide tracked caps (bold/editorial) vs a quieter, tighter label. */
  eyebrowTracking: "wide" | "tight";
  swatch: string[];
}

/** Build the Google Fonts stylesheet URL for a theme's pairing. */
export function storefrontFontsHref(spec: StorefrontThemeSpec): string {
  const families = spec.fonts.google.map((f) => `family=${f}`).join("&");
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

/** Relative luminance of a #rrggbb colour (0 = black, 1 = white). */
function luminance(hex: string): number {
  const h = hex.replace("#", "");
  if (h.length < 6) return 0.5;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Pick black or white ink for text sitting on an arbitrary accent colour. */
export function inkOn(hex: string): string {
  return luminance(hex) > 0.55 ? "#0B1220" : "#FFFFFF";
}

export const STOREFRONT_THEMES: Record<StorefrontTheme, StorefrontThemeSpec> = {
  modern: {
    label: "Corridor",
    vars: {
      bg: "#0E1A30", surface: "#16253F", surface2: "#1E3054", ink: "#F2F5FA", muted: "#93A1BA",
      accent: "#F26419", border: "rgba(255,255,255,0.10)",
      accentInk: "#FFFFFF", accent2: "#5B9DF9", positive: "#4ADE80", highlight: "#FBBF24",
    },
    hero_stops: ["#12233F", "#1B3054", "#0E1A30"],
    fonts: {
      display: "'Space Grotesk', system-ui, sans-serif",
      body: "'DM Sans', system-ui, sans-serif",
      google: ["Space+Grotesk:wght@500;700", "DM+Sans:wght@400;500;700"],
    },
    tone: "dark",
    hero: "gradient",
    eyebrowTracking: "wide",
    swatch: ["#0E1A30", "#5B9DF9", "#F26419"],
  },
  professional: {
    label: "Studio",
    vars: {
      bg: "#F6F8FB", surface: "#FFFFFF", surface2: "#ECF1F7", ink: "#101A2C", muted: "#5A6B85",
      accent: "#1D4ED8", border: "#DDE5F0",
      accentInk: "#FFFFFF", accent2: "#0EA5E9", positive: "#0F9D58", highlight: "#F59E0B",
    },
    hero_stops: ["#E7EEFA", "#F3F7FD", "#F6F8FB"],
    fonts: {
      display: "'Sora', system-ui, sans-serif",
      body: "'Manrope', system-ui, sans-serif",
      google: ["Sora:wght@600;700", "Manrope:wght@400;500;700"],
    },
    tone: "light",
    hero: "flat",
    eyebrowTracking: "tight",
    swatch: ["#101A2C", "#1D4ED8", "#0EA5E9"],
  },
  minimal: {
    label: "Paper",
    vars: {
      bg: "#FBFBF9", surface: "#FFFFFF", surface2: "#F1F0EC", ink: "#14140F", muted: "#6F6F66",
      accent: "#141410", border: "#E3E2DC",
      accentInk: "#FBFBF9", accent2: "#9A7B4F", positive: "#3F7D53", highlight: "#B98B2E",
    },
    hero_stops: ["#F4F3EE", "#FAFAF7", "#FBFBF9"],
    fonts: {
      display: "'Instrument Serif', Georgia, serif",
      body: "'Work Sans', system-ui, sans-serif",
      google: ["Instrument+Serif:ital@0;1", "Work+Sans:wght@400;500;600"],
    },
    tone: "light",
    hero: "minimal",
    eyebrowTracking: "tight",
    swatch: ["#141410", "#9A7B4F", "#F1F0EC"],
  },
  moroccan: {
    label: "Clay",
    vars: {
      bg: "#FDF6EE", surface: "#FFFFFF", surface2: "#F7E9D8", ink: "#33210F", muted: "#7A6248",
      accent: "#B4451F", border: "#EBD9C2",
      accentInk: "#FFF7F0", accent2: "#1F7A6B", positive: "#2F7D5B", highlight: "#D08C2A",
    },
    hero_stops: ["#F6E2CC", "#FBEEDF", "#FDF6EE"],
    fonts: {
      display: "'DM Serif Display', Georgia, serif",
      body: "'Fira Sans', system-ui, sans-serif",
      google: ["DM+Serif+Display", "Fira+Sans:wght@400;500;600"],
    },
    tone: "light",
    hero: "gradient",
    eyebrowTracking: "wide",
    swatch: ["#B4451F", "#1F7A6B", "#F7E9D8"],
  },
  dark: {
    label: "Onyx",
    vars: {
      bg: "#0A0C10", surface: "#14181F", surface2: "#1C222C", ink: "#F4F6F8", muted: "#8C97A6",
      accent: "#38BDF8", border: "rgba(255,255,255,0.08)",
      accentInk: "#05131C", accent2: "#A78BFA", positive: "#34D399", highlight: "#FCD34D",
    },
    hero_stops: ["#101620", "#161D29", "#0A0C10"],
    fonts: {
      display: "'Archivo', system-ui, sans-serif",
      body: "'IBM Plex Sans', system-ui, sans-serif",
      google: ["Archivo:wght@600;700", "IBM+Plex+Sans:wght@400;500;600"],
    },
    tone: "dark",
    hero: "gradient",
    eyebrowTracking: "wide",
    swatch: ["#0A0C10", "#A78BFA", "#38BDF8"],
  },
  atlas: {
    label: "Atlas",
    vars: {
      bg: "#F3F5F1", surface: "#FFFFFF", surface2: "#E7EDE4", ink: "#16241B", muted: "#5D6F60",
      accent: "#1F6F4A", border: "#D8E1D4",
      accentInk: "#F6FBF7", accent2: "#C7873A", positive: "#2E8B57", highlight: "#D3A029",
    },
    hero_stops: ["#E2EBDE", "#EEF3EB", "#F3F5F1"],
    fonts: {
      display: "'Urbanist', system-ui, sans-serif",
      body: "'Epilogue', system-ui, sans-serif",
      google: ["Urbanist:wght@600;700", "Epilogue:wght@400;500;600"],
    },
    tone: "light",
    hero: "flat",
    eyebrowTracking: "tight",
    swatch: ["#16241B", "#C7873A", "#1F6F4A"],
  },

  // ── Bright light themes ────────────────────────────────────────────
  // Studio / Paper / Clay / Atlas are all soft, low-chroma light themes and
  // read as variations of one another. These four are the loud end of the
  // light spectrum: saturated accents, higher contrast, punchier type.
  mint: {
    label: "Mint",
    vars: {
      bg: "#F2FBF6", surface: "#FFFFFF", surface2: "#DFF6EA", ink: "#062E22", muted: "#4C7A68",
      accent: "#00875A", border: "#C8E9D9",
      accentInk: "#FFFFFF", accent2: "#0F766E", positive: "#00875A", highlight: "#F2B705",
    },
    hero_stops: ["#CFF3E2", "#E8FAF1", "#F2FBF6"],
    fonts: {
      display: "'Outfit', system-ui, sans-serif",
      body: "'Figtree', system-ui, sans-serif",
      google: ["Outfit:wght@500;700", "Figtree:wght@400;500;700"],
    },
    tone: "light",
    hero: "flat",
    eyebrowTracking: "tight",
    swatch: ["#062E22", "#00875A", "#DFF6EA"],
  },
  signal: {
    label: "Signal",
    vars: {
      bg: "#FFFFFF", surface: "#FFFFFF", surface2: "#F2F2F2", ink: "#0A0A0A", muted: "#5C5C5C",
      accent: "#111111", border: "#0A0A0A",
      accentInk: "#FFE500", accent2: "#FFE500", positive: "#0B6B3A", highlight: "#FFE500",
    },
    hero_stops: ["#FFFFFF", "#FAFAFA", "#FFFFFF"],
    fonts: {
      display: "'Archivo Black', system-ui, sans-serif",
      body: "'Hind', system-ui, sans-serif",
      google: ["Archivo+Black", "Hind:wght@400;500;600"],
    },
    tone: "light",
    hero: "minimal",
    eyebrowTracking: "wide",
    swatch: ["#0A0A0A", "#FFE500", "#FFFFFF"],
  },
  lagoon: {
    label: "Lagoon",
    vars: {
      bg: "#F1FAFC", surface: "#FFFFFF", surface2: "#DDF1F7", ink: "#062A33", muted: "#4B7684",
      accent: "#0891B2", border: "#C4E6EF",
      accentInk: "#FFFFFF", accent2: "#6366F1", positive: "#0E9F6E", highlight: "#F59E0B",
    },
    hero_stops: ["#C9EAF3", "#E6F6FA", "#F1FAFC"],
    fonts: {
      display: "'Rubik', system-ui, sans-serif",
      body: "'Karla', system-ui, sans-serif",
      google: ["Rubik:wght@500;700", "Karla:wght@400;500;700"],
    },
    tone: "light",
    hero: "flat",
    eyebrowTracking: "tight",
    swatch: ["#062A33", "#0891B2", "#6366F1"],
  },
};



/** Curated accent overrides — a fixed palette, not a raw color picker, so every
 *  storefront stays legible against its theme regardless of what's chosen. */
/** Theme keys grouped by tone — the pickers render these groups so the eight
 *  light themes don't read as one undifferentiated wall of swatches. */
export const THEME_GROUPS: { id: "light" | "dark"; label: string; keys: StorefrontTheme[] }[] = [
  {
    id: "light",
    label: "Light & bright",
    keys: (Object.keys(STOREFRONT_THEMES) as StorefrontTheme[]).filter((k) => STOREFRONT_THEMES[k].tone === "light"),
  },
  {
    id: "dark",
    label: "Dark & bold",
    keys: (Object.keys(STOREFRONT_THEMES) as StorefrontTheme[]).filter((k) => STOREFRONT_THEMES[k].tone === "dark"),
  },
];

export const ACCENT_SWATCHES: { value: string; label: string }[] = [
  { value: "#F26419", label: "Orange" },
  { value: "#2563EB", label: "Blue" },
  { value: "#10B981", label: "Emerald" },
  { value: "#DC2626", label: "Red" },
  { value: "#7C3AED", label: "Violet" },
  { value: "#D97706", label: "Amber" },
  { value: "#0D9488", label: "Teal" },
  { value: "#DB2777", label: "Rose" },
  { value: "#334155", label: "Slate" },
  { value: "#9B2C2C", label: "Terracotta" },
];

/** Hero band background — a bold corridor gradient for gradient themes, a
 *  flat brand panel for flat themes, a bare strip for minimal, or a plain
 *  cover-photo scrim when the courier uploaded a photo. The one formula both
 *  the public page and the builder preview must use, so they can't drift. */
export function heroGradientFor(spec: StorefrontThemeSpec, coverUrl: string | null): string {
  const [a, b, c] = spec.hero_stops;
  if (coverUrl) return `linear-gradient(180deg, ${a} 0%, ${c} 100%)`;
  if (spec.hero === "flat") return `linear-gradient(120deg, ${a} 0%, ${b} 55%, ${c} 100%)`;
  if (spec.hero === "minimal") return `linear-gradient(180deg, ${a} 0%, ${c} 100%)`;
  return `linear-gradient(120deg, ${a} 0%, ${b} 50%, ${c} 100%)`;
}

export function resolveStorefrontTheme(
  theme: StorefrontTheme | null | undefined,
  accentOverride?: string | null,
): StorefrontThemeSpec {
  const spec = STOREFRONT_THEMES[theme as StorefrontTheme] ?? STOREFRONT_THEMES.modern;
  if (!accentOverride) return spec;
  return {
    ...spec,
    vars: { ...spec.vars, accent: accentOverride, accentInk: inkOn(accentOverride) },
  };
}

/**
 * The single place that turns a theme spec into the `--sf-*` CSS variables.
 * Both the public storefront and the builder canvas must mount these, or any
 * shared child (CoverageMap, booking dialog…) renders with unresolved vars —
 * which is what made country names invisible inside the builder.
 */
/**
 * True when the resolved theme paints a dark surface — used to pick the
 * light/dark EuroCollis wordmark so the footer never clashes with a custom
 * dark theme (the theme *name* is not a reliable signal once couriers edit
 * their own colours).
 */
export function isDarkStorefrontTheme(spec: StorefrontThemeSpec): boolean {
  const hex = String(spec?.vars?.surface ?? spec?.vars?.bg ?? "#ffffff").trim();
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
  if (!m) return false;
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Relative luminance (sRGB approximation) — below 50% reads as dark.
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5;
}

export function storefrontThemeVars(spec: StorefrontThemeSpec): Record<string, string> {
  return {
    "--sf-bg": spec.vars.bg,
    "--sf-surface": spec.vars.surface,
    "--sf-surface-2": spec.vars.surface2,
    "--sf-ink": spec.vars.ink,
    "--sf-muted": spec.vars.muted,
    "--sf-accent": spec.vars.accent,
    "--sf-accent-ink": spec.vars.accentInk,
    "--sf-accent-2": spec.vars.accent2,
    "--sf-positive": spec.vars.positive,
    "--sf-highlight": spec.vars.highlight,
    "--sf-border": spec.vars.border,
    // Intent hues — same meaning on every theme: booking is green, custom
    // requests/quotes are amber, messaging & support are blue, info is blue.
    // Action buttons are OUTLINED in these, never solid-filled.
    "--sf-intent-book": "#10B981",
    "--sf-intent-request": "#F59E0B",
    "--sf-intent-support": "#3B82F6",
    "--sf-intent-info": "#3B82F6",
    "--sf-font-display": spec.fonts.display,
    "--sf-font-body": spec.fonts.body,
  };
}

export interface FaqRow {
  id: string;
  courier_id: string;
  question: string;
  answer: string;
  sort_order: number;
  question_i18n?: LocaleMap;
  answer_i18n?: LocaleMap;
}

export interface GalleryRow {
  id: string;
  courier_id: string;
  storage_path: string;
  caption: string | null;
  sort_order: number;
  caption_i18n?: LocaleMap;
}


const RESERVED_SLUGS = new Set([
  // top-level app routes
  "admin","api","support","support-portal","courier","partner","partnerships",
  "affiliate","affiliates","track","quote","results","booking","booking-success",
  "confirmation","login","signup","signup-success","auth","help","faq","about",
  "press","status","trust","sitemap","developers","account","how-it-works",
  "for-couriers","couriers","custom-request","custom-request-booking","reset-pin",
  "reset-password","forgot-password","install","edit-request","shipping-routes",
  "guides","policy-centre","courier-profile","scan","manifest","verify",
  "pickup","benmira-foundation","review","offer",
  // short URL prefixes
  "c","r","t",
  // generic action words
  "new","edit","delete","settings","onboarding",
  // reserved hostnames / system
  "www","mail","ftp","root","null","undefined","static","public","assets",
  // language codes (must not collide with /:lang locale routes)
  "en","se","fr","es","it","ar","de","nl","pt","pl","ro","cz","sk","gr","da","fi","no","bg","si","be","ch","at","gb","ie","ma",
]);

// Slug prefixes that collide with parameterized routes (e.g. /shipping-:slug)
const RESERVED_PREFIXES = ["shipping-", "courier-to-", "courier-from-"];

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export function isReservedSlug(slug: string): boolean {
  const s = slug.toLowerCase();
  if (RESERVED_SLUGS.has(s)) return true;
  return RESERVED_PREFIXES.some((p) => s.startsWith(p));
}

/** Stable per-device id used to dedupe anonymous views and tie a session's
 *  actions together. Never leaves the device except as this opaque value. */
function deviceHash(): string | null {
  try {
    const k = "ec_device_id";
    let id = localStorage.getItem(k);
    if (!id) { id = crypto.randomUUID(); localStorage.setItem(k, id); }
    return id;
  } catch {
    return null; // private mode
  }
}

/**
 * Share-channel attribution.
 *
 * A share link carries `?utm_source=whatsapp&utm_medium=message`. The visitor
 * usually lands once with those params and then clicks around, so the channel
 * is latched into sessionStorage on first sight and replayed on every later
 * event of that session — otherwise only the page view would be attributed and
 * the resulting booking would look "direct".
 */
const CHANNEL_KEY = "ec_sf_channel";

function slugChannel(v: string | null): string | null {
  if (!v) return null;
  const s = v.toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 32);
  return s || null;
}

export function captureStorefrontChannel(): { source: string | null; medium: string | null } {
  if (typeof window === "undefined") return { source: null, medium: null };
  try {
    const params = new URLSearchParams(window.location.search);
    const source = slugChannel(params.get("utm_source") ?? params.get("ref"));
    const medium = slugChannel(params.get("utm_medium"));
    if (source) {
      sessionStorage.setItem(CHANNEL_KEY, JSON.stringify({ source, medium }));
      return { source, medium };
    }
    const stored = sessionStorage.getItem(CHANNEL_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    /* private mode — attribution degrades to "direct" */
  }
  return { source: null, medium: null };
}

/**
 * Single write path for storefront analytics.
 *
 * Direct table inserts are revoked: everything goes through
 * `record_storefront_event`, which validates the courier, rate limits the
 * visitor and drops a `ref_id` that doesn't resolve to a real row.
 *
 * Preferred hop is the Vercel edge route, which additionally stamps the
 * visitor's country and a salted IP hash server-side. When that route isn't
 * available (Lovable preview, local dev) we fall back to calling the RPC
 * directly — the event is still recorded, just without geo.
 */
async function recordStorefrontEvent(payload: {
  courierId: string;
  kind: "view" | StorefrontConversionKind;
  refId?: string | null;
  sessionHash: string | null;
  language: string | null;
}) {
  const channel = captureStorefrontChannel();
  const body = {
    courierId: payload.courierId,
    kind: payload.kind,
    refId: payload.refId ?? null,
    sessionHash: payload.sessionHash,
    language: payload.language,
    source: channel.source,
    medium: channel.medium,
  };
  // `/api/storefront-track` only exists on the hosting edge. In local dev it
  // 404s on every storefront view, which pollutes the console and fails the
  // "no console errors" regression net — go straight to the RPC there.
  if (!import.meta.env.DEV) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("/api/storefront-track", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify(body),
        keepalive: true,
      });
      if (res.ok) return;
    } catch {
      /* fall through to the direct RPC */
    }
  }

  try {
    await supabase.rpc("record_storefront_event" as any, {
      p_courier_id: body.courierId,
      p_kind: body.kind,
      p_ref_id: body.refId,
      p_session_hash: body.sessionHash,
      p_language: body.language,
      p_country: null,
      p_ip_hash: null,
      p_source: body.source,
      p_medium: body.medium,
    } as any);
  } catch {
    /* analytics is best-effort */
  }
}


/** Record a deduped view (per user OR per device per day). Safe to call from the public page. */
export async function trackStorefrontView(courierId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  await recordStorefrontEvent({
    courierId,
    kind: "view",
    sessionHash: user ? null : deviceHash(),
    // Visitor's own browser language — not the storefront's current i18n
    // state, which may just be the site default a first-time visitor
    // never chose. Feeds the "language used" breakdown in courier analytics.
    // Stored as the BASE tag only ("fr", not "fr-CA") so the report is not
    // fragmented across regional variants.
    language: baseLanguageTag(typeof navigator !== "undefined" ? navigator.language : null),
  });
}

/** "fr-CA" → "fr". Keeps analytics from splitting one language into variants. */
export function baseLanguageTag(tag: string | null | undefined): string | null {
  if (!tag) return null;
  const base = tag.toLowerCase().split("-")[0].trim();
  return base || null;
}

/** Actions a visitor took FROM the storefront, so a page view can be tied to
 *  an outcome. Fire-and-forget: an analytics write must never block the UI. */
export type StorefrontConversionKind =
  | "book_click"
  | "message_click"
  | "custom_request"
  | "lead_submitted"
  /** Storefront-native booking committed on the courier's own page. */
  | "booking";

export async function trackStorefrontConversion(
  courierId: string,
  kind: StorefrontConversionKind,
  refId?: string | null,
) {
  await recordStorefrontEvent({
    courierId,
    kind,
    refId: refId ?? null,
    sessionHash: deviceHash(),
    language: baseLanguageTag(typeof navigator !== "undefined" ? navigator.language : null),
  });
}



/**
 * The `courier-storefront-media` bucket is private — public URLs 400.
 * Use `signStorefrontMedia` / `useStorefrontMediaUrl` from `@/lib/storefrontMedia`
 * (or the `StorefrontMediaImg` component) instead.
 */
/** Languages the public storefront is served in. */
export const STOREFRONT_LANGUAGES = ["en", "fr", "es", "it", "ar", "se"] as const;
export type StorefrontLanguage = (typeof STOREFRONT_LANGUAGES)[number];

export interface TranslationHealth {
  /** Language the courier wrote in — never counted as "to translate". */
  source: string;
  /** Languages that still need a translation pass. */
  targets: string[];
  /** Of `targets`, the ones fully covered across every authored field. */
  covered: string[];
  missing: string[];
  /** No authored copy at all — nothing to translate, so the badge stays quiet. */
  empty: boolean;
}

/**
 * Which platform languages a courier's OWN words are actually available in.
 *
 * `translate-storefront` can fail silently (network, model, quota), which used
 * to leave a French visitor reading Darija forever with nothing in the builder
 * saying so. This recomputes coverage from the cached `*_i18n` maps, so the
 * builder can show "3 of 5 languages translated" and offer a retry.
 */
export function translationHealth(
  sf: Partial<StorefrontRow>,
  faqs: FaqRow[] = [],
  gallery: GalleryRow[] = [],
): TranslationHealth {
  const source = (sf.source_language || "en").slice(0, 2).toLowerCase();
  const targets = STOREFRONT_LANGUAGES.filter((l) => l !== source);

  // Every authored string paired with the locale map that should carry it.
  const pairs: { text: string | null | undefined; map: LocaleMap | undefined }[] = [
    { text: sf.tagline, map: sf.tagline_i18n },
    { text: sf.hero_headline, map: sf.hero_headline_i18n },
    { text: sf.bio_richtext, map: sf.bio_i18n },
    { text: sf.about_richtext, map: sf.about_i18n },
  ];

  const copy = sf.section_copy || {};
  const copyI18n = sf.section_copy_i18n || {};
  for (const [section, entry] of Object.entries(copy)) {
    if (!entry) continue;
    const maps = (copyI18n as any)[section] || {};
    for (const field of ["eyebrow", "title", "subtitle"] as const) {
      pairs.push({ text: (entry as any)[field], map: maps[field] });
    }
  }
  for (const f of faqs) {
    pairs.push({ text: f.question, map: f.question_i18n });
    pairs.push({ text: f.answer, map: f.answer_i18n });
  }
  for (const g of gallery) {
    pairs.push({ text: g.caption, map: g.caption_i18n });
  }

  const authored = pairs.filter((p) => typeof p.text === "string" && p.text.trim().length > 0);
  if (authored.length === 0) {
    return { source, targets, covered: [...targets], missing: [], empty: true };
  }

  const covered = targets.filter((lang) =>
    authored.every((p) => {
      const hit = p.map?.[lang];
      return typeof hit === "string" && hit.trim().length > 0;
    }),
  );
  const missing = targets.filter((l) => !covered.includes(l));
  return { source, targets, covered, missing, empty: false };
}
